// ============================================================================
// AttendanceControls — the shared attendance / check-in panel for a session.
// ----------------------------------------------------------------------------
// Reused by BOTH the dashboard calendar appointment detail (SessionDetailSheet)
// AND the session log/detail screen (pages/sessions/SessionLog), so attendance
// behaves identically wherever a trainer opens a session.
//
// Shows the current attendance status (scheduled / attended / no-show /
// cancelled) and, when attended, the recorded check-in time. Offers three
// trainer actions for a session that hasn't been attended yet:
//   • Mark arrived — records a trainer-side check-in (optional time picker that
//     defaults to "now"); the backend flips the session to attended + completed.
//   • No-show — marks the session a no-show (excluded from income).
//   • Show check-in QR — mints a token (POST /sessions/{id}/checkin-qr) and
//     renders the returned public URL as a QR for the client to scan in person.
//
// Mobile-first, Pulse-styled, bilingual (en / 繁中) via the `attendance` NS.
// Reduced-motion-safe (no entrance animation needed; transitions collapse via
// global.css). Self-contained: the parent only passes session identity + state.
// ============================================================================
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, CheckinQr } from ".";
import type { BadgeTone } from "./Badge";
import { useCheckinQr, useCheckinSession, useNoShowSession } from "../hooks/sessions";
import type { AttendanceStatus } from "../api";
import { formatDateTimeLong } from "../lib/date";

export interface AttendanceControlsProps {
  sessionId: number;
  /** Current attendance (NULL backend column should already be "scheduled"). */
  attendance: AttendanceStatus;
  /** ISO-8601 UTC instant of the recorded check-in, when attended. */
  checkedInAt?: string | null;
  /** Client's display name — used in the QR prompt copy. */
  clientName?: string;
  /** Called after a successful attendance mutation (e.g. to close a sheet). */
  onChanged?: () => void;
}

const TONE: Record<AttendanceStatus, BadgeTone> = {
  scheduled: "neutral",
  attended: "success",
  no_show: "warning",
  cancelled: "danger",
};

/** Local <datetime-local> "now" string (device wall-clock), minute precision. */
function nowLocalInput(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}` +
    `T${p(d.getHours())}:${p(d.getMinutes())}`
  );
}

/** A device-local <datetime-local> value → the tz-aware UTC ISO the API stores. */
function localToIso(v: string): string | null {
  const s = v.trim();
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function AttendanceControls({
  sessionId,
  attendance,
  checkedInAt,
  clientName,
  onChanged,
}: AttendanceControlsProps) {
  const { t } = useTranslation("attendance");
  const checkin = useCheckinSession();
  const noShow = useNoShowSession();
  const qr = useCheckinQr();

  // Optional arrival-time picker (collapsed by default; defaults to now).
  const [timeOpen, setTimeOpen] = useState(false);
  const [arrival, setArrival] = useState("");
  // QR panel: holds the minted public URL once "Show check-in QR" succeeds.
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | undefined>();

  const busy = checkin.isPending || noShow.isPending;
  const isAttended = attendance === "attended";
  const isClosed = attendance === "cancelled";

  const onMarkArrived = () => {
    setError(undefined);
    const iso = timeOpen ? localToIso(arrival) : null;
    checkin.mutate(
      { sessionId, body: iso ? { checked_in_at: iso } : {} },
      {
        onSuccess: () => onChanged?.(),
        onError: () => setError(t("error")),
      },
    );
  };

  const onNoShow = () => {
    setError(undefined);
    noShow.mutate(sessionId, {
      onSuccess: () => onChanged?.(),
      onError: () => setError(t("error")),
    });
  };

  const onToggleQr = () => {
    setError(undefined);
    if (qrUrl) {
      setQrUrl(null);
      return;
    }
    qr.mutate(sessionId, {
      onSuccess: (res) => setQrUrl(res.checkin_url),
      onError: () => setError(t("qrError")),
    });
  };

  const checkedInLabel =
    isAttended && checkedInAt
      ? t("checkedInOn", { datetime: formatDateTimeLong(checkedInAt) })
      : undefined;

  return (
    <section
      aria-label={t("heading")}
      className="rounded-lg bg-surface-soft p-4 ring-1 ring-line"
    >
      {/* Heading + current status */}
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("heading")}
        </p>
        <Badge tone={TONE[attendance]} dot solid={isAttended}>
          {t(`status.${attendance}`)}
        </Badge>
      </div>

      {checkedInLabel && (
        <p className="mt-2 text-sm font-medium text-lime-600">
          {checkedInLabel}
        </p>
      )}

      {/* Actions — only while the session is still open (not cancelled). */}
      {!isClosed && (
        <div className="mt-3 space-y-3">
          {!isAttended && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  onClick={onMarkArrived}
                  disabled={busy}
                  iconLeft={<IconCheck />}
                >
                  {checkin.isPending ? t("markingArrived") : t("markArrived")}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onNoShow}
                  disabled={busy}
                  className="!text-amber-600"
                  iconLeft={<IconNoShow />}
                >
                  {noShow.isPending ? t("markingNoShow") : t("noShow")}
                </Button>
              </div>

              {/* Optional arrival-time picker (defaults to now). */}
              <div>
                <button
                  type="button"
                  onClick={() => {
                    const next = !timeOpen;
                    setTimeOpen(next);
                    if (next && !arrival) setArrival(nowLocalInput());
                  }}
                  aria-expanded={timeOpen}
                  className="inline-flex items-center gap-1 text-sm font-bold text-coral underline-offset-2 hover:text-coral-600 focus-visible:outline-none focus-visible:underline"
                >
                  <IconClock />
                  {t("arrivalTime")}
                </button>
                {timeOpen && (
                  <div className="mt-2">
                    <input
                      type="datetime-local"
                      value={arrival}
                      onChange={(e) => setArrival(e.target.value)}
                      aria-label={t("arrivalTime")}
                      className="h-11 w-full rounded-md bg-surface px-3 text-base text-body ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
                    />
                    <p className="mt-1 text-xs text-body-soft">
                      {t("arrivalTimeHint")}
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Show / hide the check-in QR (available regardless of status). */}
          <div>
            <Button
              size="sm"
              variant="ghost"
              onClick={onToggleQr}
              disabled={qr.isPending}
              iconLeft={<IconQr />}
            >
              {qr.isPending
                ? t("generatingQr")
                : qrUrl
                  ? t("hideQr")
                  : t("showQr")}
            </Button>

            {qrUrl && (
              <div className="mt-3 rounded-lg bg-surface p-4 ring-1 ring-line">
                <p className="mb-1 text-center font-display text-sm font-bold text-body">
                  {t("qrTitle")}
                </p>
                <p className="mb-3 text-center text-xs text-body-soft">
                  {clientName
                    ? t("qrBlurb", { name: clientName })
                    : t("qrBlurbGeneric")}
                </p>
                <CheckinQr url={qrUrl} label={t("qrAria")} />
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={() =>
                      qr.mutate(sessionId, {
                        onSuccess: (res) => setQrUrl(res.checkin_url),
                        onError: () => setError(t("qrError")),
                      })
                    }
                    disabled={qr.isPending}
                    className="text-xs font-bold text-body-soft underline-offset-2 hover:text-coral hover:underline focus-visible:outline-none focus-visible:underline disabled:opacity-50"
                  >
                    {t("qrRegenerate")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 text-sm font-medium text-danger" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

// --- local glyphs (Pulse stroke weight; inherit currentColor) ---------------

function IconCheck() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconNoShow() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 8l8 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 7.5V12l3 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconQr() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
      <path
        d="M4 4h5v5H4V4Zm0 11h5v5H4v-5ZM15 4h5v5h-5V4Zm0 11h2v2m3 0v3m-3 0h-2v-3m5 0v3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
